import React, { useEffect, useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import { useSelector } from 'react-redux';
import AppState from '../../../models/redux/state/app.state';
import { BASE_URL, IMAGE_PATHS } from '../../../constants/paths.constants';
import history from '../../../services/browserHistory';
import HomeFooter from '../../micro/HomeFooter/HomeFooter';
import HomeNavBar from '../../micro/HomeNavBar/HomeNavBar';
import AdminPermissions from './AdminPermissions';
import AdminSideNavigation, { AdminSection } from './AdminSideNavigation';
import ExportAnalytics from './ExportAnalytics';
import InviteForm from './InviteForm';

const useStyles = makeStyles((theme: Theme) => ({
  portalPage: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  pageBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  content: {
    flex: 1,
    minWidth: 0,
    padding: '17px 65px 65px',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      padding: '17px 25px 45px',
    },
  },
  images: {
    display: 'block',
    width: '100%',
  },
}));

const AdminPortal = (): JSX.Element => {
  const classes = useStyles();
  const canManageAdmins = Boolean(useSelector((state: AppState) => state.auth.user?.canManageAdmins));
  const [activeSection, setActiveSection] = useState<AdminSection>('analytics');
  const [wasPageReloaded] = useState(() => {
    const navigationEntry = window.performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;

    return navigationEntry
      ? navigationEntry.type === 'reload'
      : window.performance.navigation.type === window.performance.navigation.TYPE_RELOAD;
  });

  useEffect(() => {
    document.title = 'Admin Portal';
  }, []);

  useEffect(() => {
    if (activeSection === 'permissions' && !canManageAdmins) {
      setActiveSection('analytics');
    }
  }, [activeSection, canManageAdmins]);

  const goBack = () => {
    const locationState = history.location.state as { from?: string } | undefined;

    if (wasPageReloaded || !locationState?.from) {
      history.push('/trainings');
      return;
    }

    history.goBack();
  };

  return (
    <div className={classes.portalPage}>
      <HomeNavBar />
      <div className={classes.pageBody}>
        <AdminSideNavigation
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          onBack={goBack}
          canManageAdmins={canManageAdmins}
        />
        <main className={classes.content}>
          {activeSection === 'analytics' && <ExportAnalytics />}
          {activeSection === 'invite' && <InviteForm />}
          {activeSection === 'permissions' && canManageAdmins && <AdminPermissions />}
        </main>
      </div>
      <div>
        <img
          className={classes.images}
          src={`${BASE_URL}${IMAGE_PATHS.TRICOLOR_BAR.path}`}
          alt={IMAGE_PATHS.TRICOLOR_BAR.alt}
        />
        <HomeFooter />
      </div>
    </div>
  );
};

export default AdminPortal;
